// Senior Management Review — a slick, read-only, report-styled view of a
// submitted Weekly Site Report (not a form). Reviewers can leave an optional
// comment per section, raise a tracked Action straight off a comment, and
// make ONE overall Accept / Decline decision at the end. Decline requires a
// reason (overall comment, or at least one section comment/action).
import { useState } from "react";
import { toast } from "sonner";
import { MessageSquarePlus, Plus, Loader2, Send, CheckCircle2, XCircle, Camera } from "lucide-react";
import { api, formatApiError, API } from "@/lib/api";
import SignaturePad from "@/components/SignaturePad";
import { RisksTable, PhotoGrid } from "./WsrKit";
import {
    OVERVIEW_ITEMS, QUALITY_CHECKLIST_ITEMS, QUALITY_CHECKLIST_LABELS, HS_CHECKLIST_ITEMS,
    ENVIRONMENTAL_CHECKLIST_ITEMS, CDM_DUTY_ITEMS, INDUCTIONS_CHECKLIST_ITEMS,
    fmtDate, fmtDateTime, fmtReportingWeek, inductionCountSummary, isBehindSchedule,
    programmeVariance, varianceTone, getIn,
} from "./wsrConstants";

const CHECK_TONE = { Yes: { bg: "#E4F1E6", fg: "#2E7D32" }, No: { bg: "#FBE3E1", fg: "#B3261E" }, "N/A": { bg: "#F0ECE5", fg: "#8B867E" } };
const VARIANCE_TONE_COLOR = { green: "#2E7D32", red: "#B3261E", neutral: "#8B867E" };

function RLabel({ children }) { return <div className="text-[10.5px] font-semibold uppercase tracking-[.06em] text-[#9A968F]">{children}</div>; }
function RValue({ children }) { return <div className="text-[13.5px] text-[#1F1F1F] leading-relaxed mt-0.5">{children || <span className="text-[#C2BDB4] italic">—</span>}</div>; }
function RTile({ label, value, note }) {
    return (
        <div className="rounded-xl bg-[#FAF9F6] p-3">
            <RLabel>{label}</RLabel>
            <div className="text-[13.5px] font-bold text-[#1F1F1F] mt-0.5">{value || "—"}</div>
            {note && <div className="text-[11.5px] text-[#8B867E] mt-1">{note}</div>}
        </div>
    );
}
function RTable({ columns, rows }) {
    const list = rows || [];
    if (!list.length) return <p className="text-[12.5px] text-[#9A968F] italic">None recorded.</p>;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-[12.5px] border-collapse">
                <thead>
                    <tr>{columns.map((c) => <th key={c.key} className="text-left font-semibold uppercase text-[10px] tracking-[.05em] text-[#9A968F] border-b border-[#EDEAE4] pb-1.5 pr-4 whitespace-nowrap">{c.label}</th>)}</tr>
                </thead>
                <tbody>
                    {list.map((r, i) => (
                        <tr key={r.id || i} className="border-b border-[#F5F3F0] last:border-0">
                            {columns.map((c) => <td key={c.key} className="py-2 pr-4 text-[#1F1F1F] align-top">{r[c.key] ?? "—"}</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
function RChecklist({ items, labels, value }) {
    return (
        <div className="grid sm:grid-cols-2 gap-x-6">
            {items.map((item) => {
                const entry = (value || {})[item] || {};
                const tone = CHECK_TONE[entry.value] || { bg: "#F5F3F0", fg: "#9A968F" };
                return (
                    <div key={item} className="flex items-center justify-between gap-2 py-1.5 border-b border-[#F5F3F0]">
                        <span className="text-[12.5px] text-[#1F1F1F]">{labels?.[item] || item}</span>
                        <span className="flex items-center gap-2 shrink-0">
                            {entry.note && <span className="text-[11px] text-[#8B867E] italic max-w-[140px] truncate" title={entry.note}>{entry.note}</span>}
                            <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold" style={{ background: tone.bg, color: tone.fg }}>{entry.value || "—"}</span>
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

function InlineActionForm({ onCreate, busy }) {
    const [show, setShow] = useState(false);
    const [form, setForm] = useState({ title: "", description: "", owner: "", due_date: "", priority: "Medium" });
    if (!show) {
        return (
            <button type="button" data-testid="wsr-review-raise-action-toggle" onClick={() => setShow(true)}
                    className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#C75A1B] hover:underline">
                <Plus size={12} /> Raise action
            </button>
        );
    }
    return (
        <div className="rounded-lg border border-[#E5E0DA] bg-white p-2.5 space-y-2" data-testid="wsr-review-action-form">
            <input data-testid="wsr-review-action-title" placeholder="Action title" value={form.title}
                   onChange={(e) => setForm({ ...form, title: e.target.value })}
                   className="w-full rounded-lg border border-[#E5E0DA] px-2.5 py-1.5 text-[12.5px]" />
            <textarea data-testid="wsr-review-action-description" placeholder="Description (optional)" value={form.description} rows={2}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="w-full rounded-lg border border-[#E5E0DA] px-2.5 py-1.5 text-[12.5px]" />
            <div className="grid grid-cols-3 gap-2">
                <input data-testid="wsr-review-action-owner" placeholder="Assignee" value={form.owner}
                       onChange={(e) => setForm({ ...form, owner: e.target.value })} className="rounded-lg border border-[#E5E0DA] px-2.5 py-1.5 text-[12.5px]" />
                <input type="date" data-testid="wsr-review-action-due" value={form.due_date}
                       onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-lg border border-[#E5E0DA] px-2.5 py-1.5 text-[12.5px]" />
                <select data-testid="wsr-review-action-priority" value={form.priority}
                        onChange={(e) => setForm({ ...form, priority: e.target.value })} className="rounded-lg border border-[#E5E0DA] px-2.5 py-1.5 text-[12.5px]">
                    {["Low", "Medium", "High", "Critical"].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
            </div>
            <div className="flex items-center gap-2">
                <button type="button" data-testid="wsr-review-action-submit" disabled={!form.title.trim() || busy}
                        onClick={async () => { await onCreate(form); setForm({ title: "", description: "", owner: "", due_date: "", priority: "Medium" }); setShow(false); }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-[12px] font-semibold disabled:opacity-50" style={{ background: "#C75A1B" }}>
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add action
                </button>
                <button type="button" data-testid="wsr-review-action-cancel" onClick={() => setShow(false)} className="text-[11.5px] font-semibold text-[#9A968F] hover:text-[#1F1F1F]">Cancel</button>
            </div>
        </div>
    );
}

function RSection({ n, title, sectionKey, comments, setComment, onAddAction, actionBusyFor, children }) {
    const entry = comments[sectionKey] || {};
    const hasFeedback = !!(entry.comment || "").trim() || (entry.actions || []).length > 0;
    const [open, setOpen] = useState(false);
    return (
        <section id={`wsr-section-${n}`} data-testid={`wsr-review-section-${n}`} className="py-6 border-b border-[#EDEAE4] last:border-0 space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <h3 className="text-[15.5px] font-bold text-[#1F1F1F]"><span style={{ color: "#C75A1B" }}>{n}</span> {title}</h3>
                <button type="button" data-testid={`wsr-review-comment-toggle-${n}`} onClick={() => setOpen((o) => !o)}
                        className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold rounded-full px-2.5 py-1 border transition-colors"
                        style={hasFeedback ? { borderColor: "#C75A1B", color: "#C75A1B", background: "#FBF3EC" } : { borderColor: "#E5E0DA", color: "#9A968F" }}>
                    <MessageSquarePlus size={13} /> {hasFeedback ? "Comment added" : "Add comment"}
                </button>
            </div>
            <div className="space-y-3">{children}</div>
            {(open || hasFeedback) && (
                <div data-testid={`wsr-review-comment-box-${n}`} className="rounded-xl bg-[#FAF9F6] border border-[#EDEAE4] p-3 space-y-2">
                    <textarea data-testid={`wsr-review-comment-${n}`} value={entry.comment || ""} rows={2}
                              placeholder="Comment on this section (optional)…"
                              onChange={(e) => setComment(sectionKey, e.target.value)}
                              className="w-full rounded-lg border border-[#E5E0DA] bg-white px-2.5 py-1.5 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-[#C75A1B]/25" />
                    {(entry.actions || []).length > 0 && (
                        <div className="space-y-1">
                            {entry.actions.map((a) => (
                                <div key={a.id} data-testid={`wsr-review-section-action-${a.id}`} className="flex items-center gap-2 text-[11.5px] text-[#6F6A64] bg-white rounded-lg px-2.5 py-1.5 border border-[#F0ECE5] flex-wrap">
                                    <span className="font-mono text-[#8B867E]">{a.ref}</span><span className="flex-1 font-semibold text-[#1F1F1F]">{a.title}</span>
                                    <span>{a.owner || "Unassigned"}</span><span>· due {a.due_date || "—"}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <InlineActionForm onCreate={(form) => onAddAction(sectionKey, form)} busy={actionBusyFor === sectionKey} />
                </div>
            )}
        </section>
    );
}

export default function WsrReviewReport({ report, content, reportId, role, defaultTitle, onDecideRisk, onDone }) {
    const g = (p, fb) => getIn(content, p, fb);
    const [comments, setComments] = useState(() => {
        const existing = getIn(content, "sign_off.senior_review.section_comments", {}) || {};
        const copy = {};
        Object.entries(existing).forEach(([k, v]) => { copy[k] = { comment: v.comment || "", actions: v.actions || [] }; });
        return copy;
    });
    const [decision, setDecision] = useState("accepted");
    const [overallComment, setOverallComment] = useState("");
    const [title, setTitle] = useState(defaultTitle || "");
    const [signature, setSignature] = useState("");
    const [busy, setBusy] = useState(false);
    const [actionBusyFor, setActionBusyFor] = useState(null);

    const setComment = (key, text) => setComments((c) => ({ ...c, [key]: { ...(c[key] || { actions: [] }), comment: text } }));

    const addAction = async (sectionKey, form) => {
        setActionBusyFor(sectionKey);
        try {
            const { data } = await api.post(`/weekly-reports/${reportId}/review/actions`, { section: sectionKey, ...form });
            setComments((c) => ({ ...c, [sectionKey]: { ...(c[sectionKey] || {}), actions: [...((c[sectionKey] || {}).actions || []), data.action] } }));
            toast.success(`Action ${data.action.ref} raised`);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setActionBusyFor(null); }
    };

    const hasAnyFeedback = Object.values(comments).some((c) => (c.comment || "").trim() || (c.actions || []).length > 0);

    const submitDecision = async () => {
        if (decision === "returned" && !overallComment.trim() && !hasAnyFeedback) {
            toast.error("Please add an overall comment, or a comment on at least one section, explaining why this report is being declined");
            return;
        }
        setBusy(true);
        try {
            const section_comments = {};
            Object.entries(comments).forEach(([k, v]) => { if ((v.comment || "").trim() || (v.actions || []).length) section_comments[k] = v; });
            const { data } = await api.post(`/weekly-reports/${reportId}/review`, {
                status: decision, comments: overallComment, reviewer_title: title, signature: signature || null, section_comments,
            });
            toast.success(decision === "returned" ? "Report declined and returned to the Site Manager" : "Report accepted");
            onDone?.(data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const pva = g("programme.planned_vs_actual", {});
    const behind = isBehindSchedule(pva);
    const variance = programmeVariance(pva);
    const delay = g("programme.delay", {});
    const impact = g("programme.programme_impact", {});
    const rag = g("programme.rag", {});
    const competence = g("inductions.competence", {});
    const signOff = g("sign_off", {});

    return (
        <div data-testid="wsr-review-report" className="bg-white rounded-2xl border border-[#E5E0DA] shadow-brand-sm overflow-hidden">
            <div className="px-6 sm:px-10 pt-8 pb-6 border-b-[3px]" style={{ borderColor: "#C75A1B" }}>
                <div className="text-[10.5px] font-bold tracking-[.1em] uppercase text-[#9A968F]">Weekly Site Report — for your review</div>
                <h2 className="text-2xl font-bold text-[#1F1F1F] mt-1">{report.project_name}</h2>
                <p className="text-[13px] text-[#6F6A64] mt-1">{report.report_number} · {report.client_name} · Week {fmtReportingWeek(report.week_start, report.week_end)}</p>
                <p className="text-[12px] text-[#9A968F] mt-1">Submitted by {report.submitted_by_name} on {fmtDateTime(report.submitted_at)}</p>
            </div>

            <div className="px-6 sm:px-10">
                <RSection n="1.0" title="Weekly Report Overview" sectionKey="overview" comments={comments} setComment={setComment} onAddAction={addAction} actionBusyFor={actionBusyFor}>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                        {OVERVIEW_ITEMS.map((it) => (
                            <RTile key={it.key} label={it.label} value={it.optionLabels?.[g(`overview.${it.key}.selected`)] || g(`overview.${it.key}.selected`)} note={g(`overview.${it.key}.note`)} />
                        ))}
                    </div>
                </RSection>

                <RSection n="2.0" title="Executive Summary" sectionKey="exec_summary" comments={comments} setComment={setComment} onAddAction={addAction} actionBusyFor={actionBusyFor}>
                    <RLabel>Key achievements this week</RLabel><RValue>{g("exec_summary.key_achievements")}</RValue>
                    <RLabel>Key concerns / escalations</RLabel><RValue>{g("exec_summary.key_concerns")}</RValue>
                    <RLabel>Outlook</RLabel><RValue>{g("exec_summary.outlook")}</RValue>
                </RSection>

                <RSection n="3.0" title="Programme Update & Status" sectionKey="programme" comments={comments} setComment={setComment} onAddAction={addAction} actionBusyFor={actionBusyFor}>
                    <div className="grid sm:grid-cols-4 gap-2.5">
                        <RTile label="Planned %" value={pva.planned_pct ? `${pva.planned_pct}%` : "—"} />
                        <RTile label="Actual %" value={pva.actual_pct ? `${pva.actual_pct}%` : "—"} />
                        <RTile label="Variance" value={<span style={{ color: VARIANCE_TONE_COLOR[varianceTone(variance)] }}>{variance !== null ? `${variance > 0 ? "+" : ""}${variance} pts` : "—"}</span>} />
                        <RTile label="Status" value={behind ? "Behind schedule" : "On / ahead of plan"} />
                    </div>
                    <RTable columns={[{ key: "work_package", label: "Work package" }, { key: "planned_pct", label: "Planned %" }, { key: "actual_pct", label: "Actual %" }, { key: "variance", label: "Variance" }, { key: "status", label: "Status" }]}
                            rows={g("programme.works_progress", [])} />
                    {behind && (
                        <div className="grid sm:grid-cols-2 gap-3 pt-1">
                            <RTile label="Delay reason" value={delay.reason} note={delay.note} />
                            <RTile label="Programme impact (days lost)" value={impact.days_lost} note={impact.notes} />
                        </div>
                    )}
                    <RLabel>Time / Cost / Information — RAG</RLabel>
                    <RTable columns={[{ key: "area", label: "Area" }, { key: "rag", label: "RAG" }, { key: "note", label: "Commentary" }]}
                            rows={[["time", "Time"], ["cost", "Cost"], ["information", "Information / Procurement"]].map(([k, lbl]) => ({ area: lbl, rag: (rag[k] || {}).rag || "—", note: (rag[k] || {}).note || "—" }))} />
                </RSection>

                <RSection n="4.0" title="Resources On Site" sectionKey="resources" comments={comments} setComment={setComment} onAddAction={addAction} actionBusyFor={actionBusyFor}>
                    <RLabel>Resource register</RLabel>
                    <RTable columns={[{ key: "category", label: "Category" }, { key: "count", label: "Avg this week" }, { key: "vs_last_week", label: "vs last week" }, { key: "comments", label: "Comments" }]}
                            rows={g("resources.register", [])} />
                    <RLabel>Subcontractor performance review</RLabel>
                    <RTable columns={[{ key: "subcontractor", label: "Subcontractor" }, { key: "trade", label: "Trade" }, { key: "days_on_site", label: "Days on site" }, { key: "attendance", label: "Attend." }, { key: "quality", label: "Quality" }, { key: "hs", label: "H&S" }, { key: "issues", label: "Issues / action" }]}
                            rows={g("resources.subcontractor_reviews", [])} />
                    <RValue>{g("resources.narrative")}</RValue>
                </RSection>

                <RSection n="5.0" title="Plant / Equipment On Site" sectionKey="plant" comments={comments} setComment={setComment} onAddAction={addAction} actionBusyFor={actionBusyFor}>
                    {g("plant.no_plant_on_site") ? (
                        <p className="text-[12.5px] text-[#8B867E] italic">No plant or equipment on site this week.</p>
                    ) : (
                        <RTable columns={[{ key: "item", label: "Item" }, { key: "category", label: "Category" }, { key: "plant_no", label: "Plant no." }, { key: "owner_hire", label: "Owner / hire" }, { key: "status", label: "Status" }, { key: "action_taken", label: "Action taken" }, { key: "comments", label: "Comments" }]}
                                rows={g("plant.items", [])} />
                    )}
                    <RValue>{g("plant.narrative")}</RValue>
                </RSection>

                <RSection n="6.0" title="Quality Inspections" sectionKey="quality" comments={comments} setComment={setComment} onAddAction={addAction} actionBusyFor={actionBusyFor}>
                    <RLabel>Inspection log</RLabel>
                    <RTable columns={[{ key: "date", label: "Date" }, { key: "inspection", label: "Inspection" }, { key: "itp_stage", label: "ITP stage" }, { key: "owner", label: "Owner" }, { key: "result", label: "Result / follow-up" }]}
                            rows={g("quality.log", [])} />
                    <RChecklist items={QUALITY_CHECKLIST_ITEMS} labels={QUALITY_CHECKLIST_LABELS} value={g("quality.checklist", {})} />
                    <RValue>{g("quality.narrative")}</RValue>
                </RSection>

                <RSection n="7.0" title="Health & Safety Inspections" sectionKey="health_safety" comments={comments} setComment={setComment} onAddAction={addAction} actionBusyFor={actionBusyFor}>
                    <RLabel>Inspection log</RLabel>
                    <RTable columns={[{ key: "date", label: "Date" }, { key: "inspection", label: "Inspection / event" }, { key: "owner", label: "Owner" }, { key: "result", label: "Status / action" }]}
                            rows={g("health_safety.log", [])} />
                    <RLabel>Safety compliance check</RLabel>
                    <RChecklist items={HS_CHECKLIST_ITEMS} value={g("health_safety.checklist", {})} />
                    <RLabel>Environmental check</RLabel>
                    <RChecklist items={ENVIRONMENTAL_CHECKLIST_ITEMS} value={g("health_safety.environmental_checklist", {})} />
                    <RValue>{g("health_safety.narrative")}</RValue>
                </RSection>

                <RSection n="8.0" title="CDM Compliance Checklist" sectionKey="cdm" comments={comments} setComment={setComment} onAddAction={addAction} actionBusyFor={actionBusyFor}>
                    <RChecklist items={CDM_DUTY_ITEMS} value={g("cdm.duty_checklist", {})} />
                    <RLabel>Site control</RLabel>
                    <RTable columns={[{ key: "area", label: "Area" }, { key: "position", label: "Current position" }, { key: "action_owner", label: "Action / owner" }]} rows={g("cdm.site_control", [])} />
                    <RLabel>Critical actions</RLabel>
                    <RTable columns={[{ key: "action", label: "Critical action" }, { key: "status", label: "Status" }, { key: "owner_due", label: "Owner / due" }]} rows={g("cdm.critical_actions", [])} />
                    <RValue>{g("cdm.narrative")}</RValue>
                </RSection>

                <RSection n="9.0" title="Site Inductions & Toolbox Talks" sectionKey="inductions" comments={comments} setComment={setComment} onAddAction={addAction} actionBusyFor={actionBusyFor}>
                    <RLabel>Toolbox talks</RLabel>
                    <RTable columns={[{ key: "date", label: "Date" }, { key: "topic", label: "Topic" }, { key: "attendees_count", label: "# Attendees" }, { key: "attendees", label: "Attendees" }]} rows={g("inductions.toolbox_log", [])} />
                    <RLabel>{inductionCountSummary(g("inductions.induction_log", []))}</RLabel>
                    <RTable columns={[{ key: "date", label: "Date" }, { key: "name", label: "Name" }, { key: "company", label: "Company" }, { key: "person_type", label: "Person type" }]}
                            rows={g("inductions.induction_log", []).map((r) => ({ ...r, person_type: r.person_type_display || r.person_type }))} />
                    <RChecklist items={INDUCTIONS_CHECKLIST_ITEMS} value={g("inductions.checklist", {})} />
                    <div className="grid sm:grid-cols-2 gap-3">
                        <RTile label="Expired cards identified" value={competence.expired_cards} note={competence.expired_cards_detail} />
                        <RTile label="Repeat offenders" value={competence.repeat_offenders} note={competence.repeat_offenders_detail} />
                    </div>
                    <RValue>{g("inductions.narrative")}</RValue>
                </RSection>

                <RSection n="10.0" title="Risks & Issues" sectionKey="risks" comments={comments} setComment={setComment} onAddAction={addAction} actionBusyFor={actionBusyFor}>
                    <RisksTable testId="wsr-review-risks" role={role} canDecide onDecide={onDecideRisk}
                                rows={g("risks.items", [])} disabled onChange={() => {}} onDismiss={() => {}} />
                    <RValue>{g("risks.narrative")}</RValue>
                </RSection>

                <RSection n="11.0" title="Client Actions & Instructions Required" sectionKey="client_actions" comments={comments} setComment={setComment} onAddAction={addAction} actionBusyFor={actionBusyFor}>
                    <RTable columns={[{ key: "item", label: "Item" }, { key: "owner", label: "Owner" }, { key: "due_date", label: "Due" }, { key: "status", label: "Status" }]} rows={g("client_actions.items", [])} />
                    <RValue>{g("client_actions.narrative")}</RValue>
                </RSection>

                <RSection n="12.0" title="Supporting Information" sectionKey="evidence" comments={comments} setComment={setComment} onAddAction={addAction} actionBusyFor={actionBusyFor}>
                    {(g("evidence.photos", []) || []).length ? (
                        <PhotoGrid photos={g("evidence.photos", [])} apiBase={API} reportId={reportId} disabled onCaption={() => {}} onDelete={() => {}} onUpload={() => {}} />
                    ) : (
                        <p className="text-[12.5px] text-[#9A968F] italic flex items-center gap-1.5"><Camera size={13} /> No photos attached.</p>
                    )}
                    <RLabel>Attachments / linked records</RLabel>
                    <RTable columns={[{ key: "title", label: "Attachment / record" }, { key: "description", label: "Description" }, { key: "reference", label: "Reference" }]} rows={g("evidence.attachments", [])} />
                    <RValue>{g("evidence.narrative")}</RValue>
                </RSection>

                <section data-testid="wsr-review-section-13" className="py-6 space-y-2.5">
                    <h3 className="text-[15.5px] font-bold text-[#1F1F1F]"><span style={{ color: "#C75A1B" }}>13.0</span> Sign-Off (Site Manager)</h3>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <RTile label="Author" value={signOff.author_name} />
                        <RTile label="Position" value={signOff.position} />
                        <RTile label="Date" value={fmtDate(signOff.date)} />
                    </div>
                    <RLabel>Declaration</RLabel>
                    <RValue>{signOff.declaration_confirmed ? "Confirmed" : "Not confirmed"}</RValue>
                    {typeof signOff.signature === "string" && signOff.signature.startsWith("data:image/") && (
                        <img src={signOff.signature} alt="Site Manager signature" className="h-12 mt-1" />
                    )}
                </section>
            </div>

            <div className="px-6 sm:px-10 py-8 bg-[#FAF9F6] border-t border-[#EDEAE4] space-y-4" data-testid="wsr-review-decision-bar">
                <div className="text-[11px] font-semibold uppercase tracking-[.06em] text-[#9A968F]">Your decision</div>
                <div className="flex flex-col sm:flex-row gap-3">
                    <button type="button" data-testid="wsr-review-accept-btn" onClick={() => setDecision("accepted")}
                            className="flex-1 rounded-xl border-2 px-4 py-3 text-left transition-colors"
                            style={decision === "accepted" ? { borderColor: "#2E7D32", background: "#E4F1E6" } : { borderColor: "#E5E0DA", background: "#fff" }}>
                        <div className="flex items-center gap-2 font-bold text-[#2E7D32]"><CheckCircle2 size={17} /> Accept report</div>
                        <div className="text-[11.5px] text-[#6F6A64] mt-0.5">Sign off as reviewed and accepted</div>
                    </button>
                    <button type="button" data-testid="wsr-review-decline-btn" onClick={() => setDecision("returned")}
                            className="flex-1 rounded-xl border-2 px-4 py-3 text-left transition-colors"
                            style={decision === "returned" ? { borderColor: "#B3261E", background: "#FBE3E1" } : { borderColor: "#E5E0DA", background: "#fff" }}>
                        <div className="flex items-center gap-2 font-bold text-[#B3261E]"><XCircle size={17} /> Decline report</div>
                        <div className="text-[11.5px] text-[#6F6A64] mt-0.5">Return to the Site Manager to address comments and resubmit</div>
                    </button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <RLabel>Reviewer title</RLabel>
                        <input data-testid="wsr-review-title" value={title} onChange={(e) => setTitle(e.target.value)}
                               className="mt-1 w-full rounded-lg border border-[#E5E0DA] bg-white px-2.5 py-1.5 text-[13px]" />
                    </div>
                    <div>
                        <RLabel>Signature (optional)</RLabel>
                        <SignaturePad value={signature} onChange={setSignature} />
                    </div>
                </div>
                <div>
                    <RLabel>{decision === "returned" ? "Overall comment — required if no section comments were left" : "Overall comment (optional)"}</RLabel>
                    <textarea data-testid="wsr-review-overall-comment" value={overallComment} onChange={(e) => setOverallComment(e.target.value)} rows={3}
                              placeholder={decision === "returned" ? "Explain what needs to change before this can be accepted…" : "Any closing remarks…"}
                              className="mt-1 w-full rounded-lg border border-[#E5E0DA] bg-white px-2.5 py-1.5 text-[13px]" />
                </div>
                <div className="flex justify-end">
                    <button type="button" data-testid="wsr-review-submit" onClick={submitDecision} disabled={busy}
                            className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-[13px] font-semibold text-white disabled:opacity-50"
                            style={{ background: decision === "returned" ? "#B3261E" : "#2E7D32" }}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {decision === "returned" ? "Decline & send back" : "Accept report"}
                    </button>
                </div>
            </div>
        </div>
    );
}
